import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import type { SessionCommand } from '@fitting-room/contracts'
import { BrandingScope } from '../components/BrandingScope'
import { ReturnPathProvider, RoomHeader, RoomMessage, RoomSpinner } from '../components/RoomChrome'
import { ShareSheet } from '../components/ShareSheet'
import { IconSetProvider } from '../icons/registry'
import { FlowRenderer } from '../flow/FlowRenderer'
import { firstQuestionnaireStepId, flowProgress, nextStepId, previousStepId, visibleStep } from '../flow/navigation'
import { useSendCommands, useSessionView } from '../flow/useSession'
import type { FlowContext } from '../flow/types'
import { draftStore } from '../lib/drafts'
import { forgetFittingRoom, rememberFittingRoom } from '../lib/lastFittingRoom'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

export function SessionRoute() {
  const params = useParams<{ token: string }>()
  const token = params.token ?? ''

  const query = useSessionView(token)
  const { send, pending, error } = useSendCommands(token)
  const view = query.data

  const locale = view?.session.locale ?? view?.tenant.defaultLocale ?? 'en'
  const t = uiStrings(locale)

  const step = useMemo(() => (view ? visibleStep(view) : null), [view])

  // A link that opens is one this phone should be able to find again from the
  // boutique's home page; one that no longer opens should stop being offered.
  const opened = view !== undefined
  const refused = query.isError
  useEffect(() => {
    if (opened) rememberFittingRoom(token)
    else if (refused) forgetFittingRoom(token)
  }, [token, opened, refused])

  /**
   * A submitted session leaves nothing on the device.
   *
   * Not one screen's draft but the whole session's: her name, her phone number
   * and her measurements should not outlive the appointment on a phone that
   * may have been borrowed to fill the form in.
   *
   * It hangs on the session's status rather than on the submit button because
   * the status covers both halves of the ask — the submit that has just been
   * accepted (the response sets it, and nothing else does; `submit` is not
   * applied optimistically), and the link she opens again the next day on a
   * session that was already submitted before this tab existed. Depending on
   * the status and the token alone is also what makes it fire once: a refetch
   * of the same submitted session produces a new view every time and must not
   * re-run this.
   */
  const submitted = view?.session.status === 'submitted'
  useEffect(() => {
    if (!submitted) return
    draftStore(token).clearAll()
  }, [token, submitted])

  const goTo = useCallback(
    (stepId: string, commands: readonly SessionCommand[] = []) => {
      send([...commands, { type: 'goto', stepId }])
      window.scrollTo({ top: 0 })
    },
    [send],
  )

  const advance = useCallback(
    (commands: readonly SessionCommand[] = []) => {
      if (!view) return
      const next = nextStepId(view.flow, view.session.currentStepId)
      if (!next) {
        if (commands.length > 0) send(commands)
        return
      }
      goTo(next, commands)
    },
    [view, goTo, send],
  )

  const progress = useMemo(() => (view ? flowProgress(view) : null), [view])

  /*
   * The hint at the way on: once, the first time she has a gown saved on the
   * deck and the way on is her fitting room. Remembered per link on this
   * phone, so it never nags; gone when she taps it or the button, or after
   * a few seconds.
   */
  /** The share panel, dropped down from the fitting room's share mark. */
  const [sharing, setSharing] = useState(false)
  const closeShare = useCallback(() => setSharing(false), [])

  const [hintDone, setHintDone] = useState(() => hintSeen(token))
  const hintWanted = useMemo(() => {
    if (!view || !step || step.kind !== 'browse') return false
    const saved = view.session.picks.filter((p) => step.slotKeys.includes(p.slotKey)).length
    const next = view.flow.steps.find((s) => s.id === nextStepId(view.flow, view.session.currentStepId))
    return saved >= 1 && next?.kind === 'fitting_room'
  }, [view, step])
  const showHint = hintWanted && !hintDone
  const dismissHint = useCallback(() => {
    setHintDone(true)
    rememberHintSeen(token)
  }, [token])
  useEffect(() => {
    if (!showHint) return
    const timer = setTimeout(dismissHint, HINT_MS)
    return () => clearTimeout(timer)
  }, [showHint, dismissHint])

  /**
   * The screen she left when she went back to change an answer.
   *
   * Client state and nothing more. The session's cursor is the truth about
   * where she IS; this is only the memory of a door she walked through one
   * tap ago, and a reload that forgets it leaves her exactly in the flow she
   * would have had anyway — walking forward with Continue. Persisting it would
   * mean a phone put down on Tuesday still offering to go "back" on Thursday.
   */
  const [returnStepId, setReturnStepId] = useState<string | null>(null)

  const editProfile = useCallback(() => {
    if (!view || !step) return
    const first = firstQuestionnaireStepId(view.flow)
    if (!first) return
    // Where to come back to is the screen she is ON, resolved the way the
    // renderer resolved it — not `session.currentStepId`, which is empty on a
    // session nobody has moved through yet.
    setReturnStepId(step.id)
    goTo(first)
  }, [view, step, goTo])

  /**
   * The answer the screen she is on has not sent yet.
   *
   * A ref rather than state: nothing renders differently because of it, and a
   * step that re-declared it on every keystroke through `useState` would
   * re-render the whole route for a value only one callback ever reads.
   */
  const pendingExit = useRef<readonly SessionCommand[]>([])
  const setPendingExit = useCallback((commands: readonly SessionCommand[]) => {
    pendingExit.current = commands
  }, [])

  const resume = useCallback(
    (stepId: string) => {
      setReturnStepId(null)
      // Her unsent answer travels with the `goto`, so leaving sideways commits
      // exactly what Continue would have committed.
      const carried = pendingExit.current
      pendingExit.current = []
      goTo(stepId, carried.length > 0 ? carried : undefined)
    },
    [goTo],
  )

  const ctx: FlowContext | null = useMemo(() => {
    if (!view || !progress) return null // `progress` is null exactly when `view` is
    return {
      view,
      token,
      locale,
      pending,
      progress,
      send,
      advance,
      goTo,
      hasNext: nextStepId(view.flow, view.session.currentStepId) !== undefined,
      setPendingExit,
    }
  }, [view, token, locale, pending, progress, send, advance, goTo, setPendingExit])

  if (query.isPending) {
    return (
      <BrandingScope tokens={{}} className="fr-room">
        <RoomSpinner label={t.loading} />
      </BrandingScope>
    )
  }

  if (query.isError || !view || !step || !ctx) {
    return (
      <BrandingScope tokens={{}} className="fr-room">
        <RoomMessage
          title={t.errorTitle}
          detail={t.errorBody}
          action={
            <button type="button" className="fr-btn fr-btn-outline" onClick={() => void query.refetch()}>
              {t.retry}
            </button>
          }
        />
      </BrandingScope>
    )
  }

  const back = previousStepId(view.flow, view.session.currentStepId)

  /*
   * The deck is the one screen that trades its chrome for room.
   *
   * It hands its heading up to the header — the header is drawing a boutique
   * name it can spare, and the step is drawing a title it cannot afford — and
   * swaps the arrow at the top left for a labelled way back to the START of
   * the asking. An arrow beside a rack of gowns says "undo the last thing you
   * did"; what she actually wants from there is her answers.
   *
   * Decided on `step.kind`, which is a contract word rather than a screen id:
   * a flow with two decks gets it on both, and a flow with none never asks.
   */
  // The other catalogue is offered by the `line_choice` STEP, on the screen
  // before the gowns, rather than by a band over whichever question her
  // answers tripped the rule on. `view.lineOffer` is what that step reads.
  const onTheDeck = step.kind === 'browse'
  // The line she is on may name the gowns better than the step does: "Your
  // dress matches" on one catalogue, "Available for you" on the other.
  const activeLineHeading = view.flow.lines.find((l) => l.key === view.session.lineKey)?.heading ?? view.flow.lines[0]?.heading
  const headerTitle = onTheDeck ? text(activeLineHeading, locale) || text(step.title, locale) : ''

  /*
   * Whether she has enough to go on with.
   *
   * Written against `step.kind` here rather than off `onTheDeck` so the
   * narrowing is the compiler's: `slotKeys` belongs to a browse step and to no
   * other, and a flow with two decks counts each one's own slots.
   */
  const savedOnDeck = step.kind === 'browse' ? view.session.picks.filter((p) => step.slotKeys.includes(p.slotKey)).length : 0
  const readyToConverge = step.kind === 'browse' && savedOnDeck > view.overflowHintAt
  const nextIsFittingRoom = view.flow.steps.find((s) => s.id === nextStepId(view.flow, view.session.currentStepId))?.kind === 'fitting_room'

  /*
   * And the door back out, offered on every screen of the questionnaire but
   * ONLY once she has come in through it. Walking the form forward for the
   * first time there is nothing behind her to return to, and a control that
   * says otherwise would be pointing at a deck she has not reached.
   */
  const returnPath =
    returnStepId && ctx.progress.inQuestionnaire
      ? { label: t.backToBrowsing, onSelect: () => resume(returnStepId) }
      : null

  return (
    /*
      The deck is the viewport and every other screen is at least it. A form
      may be taller than the phone and must scroll; the deck scrolls inside
      itself and must not also scroll behind itself.
    */
    <BrandingScope tokens={view.tenant.branding.tokens} className={onTheDeck ? 'fr-room fr-room-fixed' : 'fr-room'}>
      <IconSetProvider set={view.tenant.iconSet}>
      <RoomHeader
        displayName={view.tenant.branding.displayName}
        title={headerTitle || undefined}
        progress={ctx.progress}
        locale={locale}
        backLabel={t.back}
        /*
         * No way back off a sent session. `goto` is a command like any other
         * and the domain closes a submitted session to all of them but
         * `profile`, so an arrow here could only ever produce "that did not
         * save" — and there is nothing behind her worth going back to: what
         * she sent is what the boutique is working from.
         */
        onBack={back && !onTheDeck && !submitted ? () => goTo(back) : undefined}
        action={
          onTheDeck
            ? { label: t.editProfile, onSelect: editProfile }
            : // Her fitting room, with something on it: the share mark, where she'll see it.
              step.kind === 'fitting_room' && view.session.picks.length > 0
              ? { label: t.shareTitle, icon: 'share', onSelect: () => setSharing(true) }
              : undefined
        }
        /*
         * The deck's way on, in the header, because the deck has no bottom to
         * put it at: the gown reaches the edge of the screen and the page does
         * not scroll.
         *
         * `ready` is the nudge that used to be a band sliding in over the
         * rails — "that's plenty to start with", with a button to dismiss it.
         * The threshold is the same one, `overflowHintAt`, resolved by the
         * server from her tier and never computed here; what changed is that
         * it now lights the control she was going to press anyway instead of
         * interrupting her to recommend it.
         */
        advance={
          onTheDeck && ctx.hasNext
            ? {
                // Where it goes, when where it goes is her fitting room.
                label: nextIsFittingRoom ? t.openFittingRoom(savedOnDeck) : t.continue,
                onSelect: () => {
                  if (showHint) dismissHint()
                  advance()
                },
                ready: readyToConverge,
                ...(showHint ? { hint: { text: t.advanceHint, onDismiss: dismissHint } } : {}),
              }
            : undefined
        }
      />

      {/*
        `min-h-0` so a screen that fills the room can also be SHORTER than what
        is inside it — which is what lets the deck's feed scroll inside a
        viewport-high column instead of growing the column to two hundred
        gowns. Without it a flex item refuses to shrink past its content and
        every `flex-1` below here is measuring the catalogue.
      */}
      <main id="fr-step" className="flex min-h-0 flex-1 flex-col">
        <ReturnPathProvider value={returnPath}>
          <FlowRenderer step={step} ctx={ctx} />
        </ReturnPathProvider>
      </main>

      {sharing ? <ShareSheet token={token} boutique={view.tenant.branding.displayName} locale={locale} onClose={closeShare} /> : null}

      <div aria-live="polite" className="sr-only">
        {pending ? t.saving : ''}
      </div>

      {error ? (
        <div
          className="fixed inset-x-0 bottom-0 z-50 px-5 py-3 text-center text-[0.8125rem]"
          role="alert"
          style={{ background: 'var(--fr-raised)', color: 'var(--fr-ink)', borderTop: '1px solid var(--fr-primary)' }}
        >
          {/* Past the appointment the refusal is final, and saying "try once
              more" would send her round a loop that can only fail. */}
          {error.code === 'session_not_editable' ? t.saveRefusedPastAppointment : t.saveFailed}
        </div>
      ) : null}
      </IconSetProvider>
    </BrandingScope>
  )
}

/** How long the hint at the way on stays up before it goes by itself. */
const HINT_MS = 9000

const hintKey = (token: string) => `fr.hint.advance.${token}`

/** Whether this phone has shown the hint for this link. Storage can be refused; then it shows once per visit. */
function hintSeen(token: string): boolean {
  try {
    return window.localStorage.getItem(hintKey(token)) === '1'
  } catch {
    return false
  }
}

function rememberHintSeen(token: string): void {
  try {
    window.localStorage.setItem(hintKey(token), '1')
  } catch {
    // Private mode, or storage refused: the state in memory covers this visit.
  }
}
